import React, { useState, useEffect, useRef, useMemo, Suspense } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Skeleton,
  CircularProgress,
  Divider,
  StepConnector,
  styled,
  stepConnectorClasses,
  StepIconProps,
  Container,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  resetStepper,
} from "../../services/redux/slices/stepperSlice";
import { AppDispatch, RootState } from "../../services/redux/store";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Summary from "../provisional-offer/ProvisionalOfferPage";
import ChatIcon from "../../assets/images/icons/message-circle-2.png";
import UploadIcon from "../../assets/images/icons/upload.png";
import { OutlinedPrimaryButton, IconButton } from "../../components/ui/Button";
import VerticalLinearStepper from "./VerticalLinearStepper";
import { setActiveHorizontalIndex } from "../../services/redux/slices/stepperDynamicFormSlice";
import {
  getFormJson,
  fetchProducts,
} from "../../services/redux/thunks/stepperDynamicFormThunks";
import { authStatusSelector } from "../../services/redux/slices/authSlice";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ProvisionalOfferPage from "../provisional-offer/ProvisionalOfferPage";
import ProvisionalOfferIcon from "../../assets/icons/mail.png";
import ViewSummary from "../summary/ViewSummary";
import { Check } from "@mui/icons-material";
import TipsIcon from "../../assets/icons/dynamicForm/bulb.png";
import HandIcon from "../../assets/icons/fi_1533908.png";
import TipsBox from "../../components/common/TipsBox";
import NoProvisionalOfferCard from "../../components/common/NoProvisionalOfferCard";
import ConnectChatComponent from "../../components/ui/ConnectChatComponent";
import { chatStatusConfig, setChatStatusConfig } from "../../services/redux/slices/chatSlice";
import chatServices from "../../services/api/service/chatService";

// ======================================================
// TYPE DEFINITIONS
// ======================================================
interface HorizontalLinearStepperProps {
  status: boolean;
  /** When SUMMARY (e.g. application summary page), hide bottom nav: Previous, Connect, Upload, Save as Draft */
  formPageType?: "FORM" | "SUMMARY";
  module?: string;
}
interface StepContent {
  label: string;
  content: React.ReactNode;
}

// ======================================================
// STYLES
// ======================================================
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: "#00B3B5",
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: "#00B3B5",
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 4,
    borderRadius: "4px",
    borderColor: "#4B465C14",
    width: "24px",
    margin: "4px",
    flex: 1,
  },
}));

// ✅ Custom Step Icon (FIX)
const CustomStepIcon: React.FC<StepIconProps> = (props) => {
  const { icon, active, completed } = props;

  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "6px",
        bgcolor: active || completed ? "#00b3b5" : "#e0e0e0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: "0.9rem",
        color: "#fff",
      }}
    >
      {completed ? <Check sx={{ color: "#fff" }} /> : icon}
    </Box>
  );
};

// ======================================================
// COMPONENT
// ======================================================
const HorizontalLinearStepper: React.FC<
  HorizontalLinearStepperProps
> = ({ status: _status, formPageType = "FORM", module }) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeStep = useSelector((state: RootState) => state.stepper.activeStep);
  const {
    horizontalSteps,
    activeHorizontalIndex,
    activeVerticalIndex,
    selectedProduct,
    loading,
    summaryApplicationEditActive,
  } = useSelector((state: RootState) => state.stepper);
  const provisionalOffer = useSelector((state: RootState) => state.stepper.breData);
  const completedHorizontalStep = useSelector((state: RootState) => state.stepper.completedHorizontalIndex);
  const applicationNumber = useSelector((state: RootState) => state.stepper.trackingId);
  const formJsonRefreshNonce = useSelector(
    (state: RootState) => state.stepper.formJsonRefreshNonce
  );
  const prevFormJsonRefreshNonceRef = useRef(0);
  const isAuthenticated = useSelector(authStatusSelector);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [isChatConnectOpen, setIsChatConnectOpen] = useState(false);
  const [isPageScrollable, setIsPageScrollable] = useState(false);
  const userProfile = useSelector((state: RootState) => state.auth.userProfile);
  const chatRef = useRef<HTMLDivElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const { chatAccess, viewAccess } = useSelector(chatStatusConfig);

  useEffect(() => {
    // In SUMMARY, avoid re-fetching on every re-mount once step config exists.
    if (formPageType === "SUMMARY" && horizontalSteps?.length > 0) {
      return;
    }
    dispatch(fetchProducts());
  }, [dispatch, formPageType, horizontalSteps?.length]);

  useEffect(() => {
    if (formPageType !== "SUMMARY") {
      return;
    }
    const summaryHasSteps = (horizontalSteps?.length ?? 0) > 0;
    const nonceAdvanced =
      formJsonRefreshNonce > prevFormJsonRefreshNonceRef.current;
    if (summaryHasSteps && !nonceAdvanced) {
      return;
    }
    prevFormJsonRefreshNonceRef.current = formJsonRefreshNonce;
    dispatch(
      getFormJson({ product: selectedProduct, trackingId: applicationNumber })
    );
  }, [
    dispatch,
    formPageType,
    horizontalSteps?.length,
    selectedProduct,
    applicationNumber,
    formJsonRefreshNonce,
  ]);

  useEffect(() => {
    if (formPageType === "SUMMARY") {
      return;
    }
    dispatch(resetStepper());
  }, [dispatch, formPageType]);

  const horizontalStepsFormSteps: any = useMemo(() => {
    const steps: StepContent[] = [];
    horizontalSteps?.forEach((elem: any) => {
      steps.push({
        label: elem?.stepLabel || elem?.horizontalStepName,
        content: (
          <>
            {VerticalLinearStepper && (
              <Suspense fallback={<div>Loading step...</div>}>
                <VerticalLinearStepper
                  steps={elem?.verticalSteps || []}
                  pageType={formPageType}
                  module={module}
                />
              </Suspense>
            )}
          </>
        ),
      });
    });

    return steps;
  }, [horizontalSteps, VerticalLinearStepper, formPageType, module]);

  const showVerticalStepper = useMemo(() => {
    return horizontalStepsFormSteps?.[activeHorizontalIndex]?.content || null;
  }, [horizontalStepsFormSteps, activeHorizontalIndex]);

  const handleNext = () => {
    if (activeHorizontalIndex < horizontalSteps.length - 1) {
      dispatch(nextStep());
    }
  };

  const handleBack = () => {
    if (activeHorizontalIndex > 0) {
      dispatch(setActiveHorizontalIndex(activeHorizontalIndex - 1));
    }
  };

  const handleSaveAsDraftAndNext = () => {
    handleNext();
  };

  const getChatFeatureStatus = async (appNum: string) => {
    try {
      const res = await chatServices.getChatFeatureStatus({
        applicationId: appNum
      });

      const responseData = res.data?.data;
      if (responseData) {
        dispatch(setChatStatusConfig({
          chatAccess: responseData.chatAccess,
          viewAccess: responseData.viewAccess,
          loading: false
        }));
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (applicationNumber) {
      getChatFeatureStatus(applicationNumber);
    }
  }, [applicationNumber]);

  // chat window open/close functions
  const handleChatConnectOpen = () => setIsChatConnectOpen(true);
  const handleChatConnectClose = () => setIsChatConnectOpen(false);

  // close chat window on outside click
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        isChatConnectOpen &&
        chatRef.current &&
        !chatRef.current.contains(event.target) &&
        !connectButtonRef.current?.contains(event.target)
      ) {
        handleChatConnectClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChatConnectOpen]);

  // prevent body scroll when chat is open
  useEffect(() => {
    if (isChatConnectOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    };
  }, [isChatConnectOpen]);

  // check if the whole document body is scrollable
  const checkPageScrollable = () => {
    const scrollable = document.body.scrollHeight > window.innerHeight;
    setIsPageScrollable(scrollable);
  };

  useEffect(() => {
    checkPageScrollable();
    window.addEventListener("resize", checkPageScrollable);

    const observer = new MutationObserver(checkPageScrollable);
    if (contentContainerRef.current) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener("resize", checkPageScrollable);
      observer.disconnect();
    };
  }, [loading, activeHorizontalIndex]);

  const SkeletonLoader = () => {
    return (
      <Box
        sx={{
          width: "100%",
          mb: { xs: 2, sm: 2 },
          pt: { xs: 1, sm: 1 },
          px: { xs: 1, sm: 0 },
        }}
      >
        {isMobile ? (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="30%" height={24} />
            </Box>
            <Box mt={2}>
              {Array.from({ length: 1 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="text"
                  height={28}
                  sx={{ my: 1 }}
                />
              ))}
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={0}
          >
            <Box sx={{ flex: 1, mr: 2 }}>
              <Stepper activeStep={0}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <Step key={index}>
                    <StepLabel>
                      <Skeleton width={100} height={20} />
                      <Skeleton width={120} height={14} />
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const SkeletonVerticalStepper = () => {
    const stepCount = 4;
    const showOnlyFormSkeleton = formPageType === "SUMMARY";

    return (
      <>
        {!showOnlyFormSkeleton && (
          <Grid container sx={{ borderBottom: "1px solid #dbdade", mb: 2 }}>
            {SkeletonLoader()}
            <Grid item xs={12} md={9}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 1,
                  mt: 2,
                }}
              >
                <Skeleton variant="text" width={"100%"} height={58} />
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  textAlign: "right",
                  mr: 2,
                  mt: 2,
                }}
              >
                <Skeleton variant="text" width={"100%"} height={20} />
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={80}
                    size={28}
                    thickness={4}
                    sx={{
                      color: "#e0e0e0",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  />
                  <CircularProgress
                    variant="indeterminate"
                    size={28}
                    thickness={4}
                    sx={{
                      color: "#00b3b5",
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Main Layout: Stepper + Form */}
        {isXsOrSm ? (
          <>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              {/* Right Form Area */}
              <Box sx={{ flex: 1, pl: 2, pr: 2, mb: 2, mt: "5px" }}>
                {/* Product Selection Dropdown */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                    }}
                  >
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="circular" width={16} height={16} />
                  </Skeleton>
                </Box>

                {/* Applicant Type Radio Buttons */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={60} height={16} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  </Box>
                </Box>

                {/* Student First Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Last Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Mobile Number */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Email */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              {!showOnlyFormSkeleton && (
                <Box
                  sx={{
                    borderRight: "1px solid #dcdddf",
                    minWidth: 120,
                    width: "140px",
                    display: "flex",
                    padding: "20px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  {Array.from({ length: stepCount }).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mb: 1,
                        ml: 1,
                        minWidth: 92,
                      }}
                    >
                      {/* Step Circle */}
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                          border: "3px solid #eee",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <Skeleton variant="circular" width={20} height={20} />
                      </Box>

                      {/* Step Label */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          maxWidth: 135,
                        }}
                      >
                        <Skeleton width={70} height={12} sx={{ mb: 1 }} />
                        {index < stepCount - 1 && (
                          <Skeleton
                            variant="rectangular"
                            width={12}
                            height={12}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Right Form Area */}
              <Box sx={{ flex: 1, pl: 2, pr: 2, mb: 2, mt: "5px" }}>
                {/* Product Selection Dropdown */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                    }}
                  >
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="circular" width={16} height={16} />
                  </Skeleton>
                </Box>

                {/* Applicant Type Radio Buttons */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={60} height={16} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  </Box>
                </Box>

                {/* Student First Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Last Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Mobile Number */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Email */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        mb: { xs: 2, sm: 2 },
        pt: { xs: 1, sm: 1 },
        px: { xs: 1, sm: 0 },
      }}
    >
      {loading ? (
        <>{SkeletonVerticalStepper()}</>
      ) : (
        <>
          {/* === Stepper === */}


          {/* Tips Box (hidden on Summary until user clicks Edit on a section) */}


          {isAuthenticated &&
            (formPageType === "SUMMARY" && !summaryApplicationEditActive) && (
              <Box
                mt={2}
                sx={{ display: activeHorizontalIndex > 1 ? "none" : "block" }}
              >
                <TipsBox
                  heading="Tips"
                  subText="Why we need your start date of your course"
                  discreption="This is the day your upcoming academic period starts and when you will need your money."
                  iconImage={TipsIcon}
                />
              </Box>
            )}


          <Box mt={formPageType === "SUMMARY" ? 0 : 3}>
            {formPageType === "SUMMARY" && !summaryApplicationEditActive
              ? null
              : showVerticalStepper}
          </Box>

          {/* Main Step Content */}

          {/* Navigation Buttons (hidden on application Summary view) */}
          {isAuthenticated &&
            formPageType !== "SUMMARY" &&
            ((activeHorizontalIndex === 1 && activeVerticalIndex !== 3) ||
              activeHorizontalIndex < 3) && (
              <Box
                mt={4}
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
                rowGap={2}
              >
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ width: isXsOrSm ? "100%" : "auto" }}
                // bgcolor={{ xs: "white", sm: "none" }}
                >
                  {activeHorizontalIndex < 2 && (
                    <>
                      <Box
                        sx={{
                          display: {
                            xs: "none",
                            sm: "block",
                          },
                        }}
                      >
                        <OutlinedPrimaryButton
                          onClick={
                            activeHorizontalIndex === 0 ||
                              activeHorizontalIndex === 3
                              ? () => { }
                              : () => handleBack
                          }
                          disabled={
                            activeHorizontalIndex === 0 ||
                            activeHorizontalIndex === 3
                          }
                        >
                          <ArrowBackIcon sx={{ mr: 1 }} />
                          Previous
                        </OutlinedPrimaryButton>
                      </Box>
                    </>
                  )}

                  <Box
                    sx={{
                      flex: { xs: 1, sm: "unset" },
                      display: { xs: "flex", sm: "unset" },
                      justifyContent: { xs: "center", sm: "unset" },
                      alignItems: { xs: "center", sm: "unset" },
                    }}
                  >
                    <IconButton
                      onClick={() => console.log("Connect clicked")}
                      sx={{
                        width: activeHorizontalIndex > 1 ? "auto" : "100%",
                      }}
                    >
                      <img
                        src={ChatIcon}
                        alt="Chat"
                        style={{ width: 20, marginRight: 8 }}
                      />
                      Connect
                    </IconButton>
                  </Box>

                  {activeHorizontalIndex < 2 && (
                    <Box
                      sx={{
                        flex: { xs: 1, sm: "unset" },
                      }}
                    >
                      <OutlinedPrimaryButton
                        onClick={() => console.log("Upload clicked")}
                        sx={{ width: "100%" }}
                      >
                        <img
                          src={UploadIcon}
                          alt="Upload"
                          style={{ width: 20, marginRight: 8 }}
                        />
                        Upload Document
                      </OutlinedPrimaryButton>
                    </Box>
                  )}
                </Box>

                {activeHorizontalIndex < 2 && (
                  <Box
                    sx={{
                      display: {
                        xs: "none", // hide on extra-small screens
                        sm: "block", // show from small screens upward
                      },
                    }}
                  >
                    <OutlinedPrimaryButton
                      onClick={handleSaveAsDraftAndNext}
                      sx={{ borderColor: "#00B3B5", color: "#00B3B5" }}
                    >
                      {activeStep === horizontalSteps.length - 1
                        ? "Finish"
                        : "Save as Draft"}
                    </OutlinedPrimaryButton>
                  </Box>
                )}
              </Box>
            )}
        </>
      )}

      {isChatConnectOpen && viewAccess && (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1000,
              width: "100%",
              height: "100%",
            }}
            onClick={handleChatConnectClose}
          />

          <Box
            ref={chatRef}
            sx={{
              zIndex: 1001,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "fixed",
              ...(isXsOrSm
                ? {
                  bottom: 80,
                  left: 0,
                  right: 0,
                  width: "100%",
                  height: "auto",
                }
                : {
                  bottom: isPageScrollable ? 100 : 80,
                  left: 130,
                }),
            }}
          >
            <ConnectChatComponent
              userName={userProfile?.name || ""}
              open={isChatConnectOpen}
              onClose={handleChatConnectClose}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default HorizontalLinearStepper;
